import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  AppBar as MuiAppBar,
  Toolbar,
  Box,
  Menu,
  Avatar,
  Divider,
  ButtonBase,
  ListItemIcon,
  ListItemText,
  styled,
  MenuItem,
  IconButton
} from '@mui/material';
import type { AccountInfo, IPublicClientApplication } from '@azure/msal-browser';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import axios from 'axios';

import { I18nNamespace } from '@/i18n/types/i18nNamespace.ts';
import type { I18nCommonNs } from '@/i18n/dictionaries/interfaces.ts';
import { useTranslation } from 'react-i18next';
import { loginRequest, graphLoginRequest } from '@/authConfig.ts';

import theme from '@/app/styles/theme.ts';
import * as UserSelectors from 'app/selectors/userSelectors.ts';
import * as GuiStateSelectors from 'app/selectors/guiStateSelectors.ts';
import { isUserSuperAdminSelector } from '@/app/selectors/userSelectors.ts';
import { useLogout } from '@/app/hooks/useLogout.ts';
import { useIsTranslatorStandalone } from '@/features/translator';
import { GuiStateActions } from '@/app/actions/guiState/guiStateAction.ts';
import SearchTrigger from './SearchTrigger.tsx';

const VadSettingsModal = React.lazy(() =>
  import('@/features/audio').then(({ VadSettingsModal }) => ({ default: VadSettingsModal }))
);
import LanguageSettingsDialog from './LanguageSettingsDialog.tsx';
import EditProfileDialog from './EditProfileDialog.tsx';
import LegalTextDialog from './LegalTextDialog.tsx'; // Added for Legal Center

import VolumeUp from '@mui/icons-material/VolumeUp';
import ExitToApp from '@mui/icons-material/ExitToApp';
import Language from '@mui/icons-material/Language';
import Domain from '@mui/icons-material/Domain';
import Person from '@mui/icons-material/Person';
import MenuIcon from '@mui/icons-material/Menu';
import MicIcon from '@mui/icons-material/Mic';
import PolicyOutlinedIcon from '@mui/icons-material/PolicyOutlined';

const AppBar = styled(MuiAppBar)({
  background: theme.palette.common.white,
  height: theme.mixins.denseToolbar.minHeight,
  borderBottom: '1px solid',
  borderBottomColor: theme.palette.grey[200]
});

type HeaderMenuItemProps = {
  icon?: React.ReactNode; // Made optional
  primaryText: string;
  secondaryText?: string;
  disabled?: boolean;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void; // Changed signature
  endIcon?: React.ReactNode; // Added for submenu indicator
};

const HeaderMenuItem: React.FC<HeaderMenuItemProps> = ({
  icon,
  primaryText,
  secondaryText,
  disabled = false,
  onClick,
  endIcon
}) => {
  return (
    <MenuItem
      disabled={disabled}
      onClick={onClick}
      sx={{
        py: 1,
        px: 2,
        '&.Mui-disabled': {
          opacity: 1,
          color: 'text.primary'
        }
      }}
    >
      <ListItemIcon sx={{ minWidth: 40, marginRight: 1, pl: 0.5 }}>{icon}</ListItemIcon>
      <ListItemText
        primary={primaryText}
        secondary={secondaryText}
        primaryTypographyProps={{
          sx: {
            color: 'text.primary',
            fontWeight: !secondaryText && disabled ? 500 : undefined,
            ...(secondaryText ? { variant: 'body2' } : {})
          }
        }}
        secondaryTypographyProps={{
          variant: 'body2',
          sx: { color: 'text.secondary' }
        }}
      />
      {endIcon && <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>{endIcon}</Box>}
    </MenuItem>
  );
};

type HeaderProps = {
  displaySidebarMenu?: boolean;
};

const getLoggedInUserPhoto = async (instance: IPublicClientApplication, account: AccountInfo) => {
  return ''; // The code below doesn't work yet

  try {
    const tokenResponse = await instance.acquireTokenSilent({ ...graphLoginRequest, account });
    const graphEndpoint = 'https://graph.microsoft.com/v1.0/me/photo/$value';
    const response = await axios(graphEndpoint, {
      headers: {
        Authorization: `Bearer ${tokenResponse.accessToken}`
      }
    });
    const avatar = Buffer.from(response.data, 'binary').toString('base64');

    return 'data:image/jpeg;base64, ' + avatar;
  } catch (e) {
    console.log('Failed to get user photo');
    return '';
  }
};

const Header: React.FC<HeaderProps> = ({ displaySidebarMenu = true }) => {
  const { accounts, instance } = useMsal();
  const logout = useLogout();
  const isAuthenticated = useIsAuthenticated();
  const dispatch = useDispatch();

  const user = useMemo(() => (accounts.length ? accounts[0] : undefined), [accounts]);
  const [userPhoto, setUserPhoto] = useState<string>('');
  const [translateCommon] = useTranslation([I18nNamespace.Common, I18nNamespace.UserManagement]);

  const currentUser = useSelector(UserSelectors.CurrentUserSelector);
  const isSuperAdmin = useSelector(isUserSuperAdminSelector);
  const sideBarOpenState = useSelector(GuiStateSelectors.sideBarStateSelector);

  const translatorStandalone = useIsTranslatorStandalone();
  const isMacLike = useMemo(() => {
    if (typeof navigator === 'undefined') {
      return false;
    }

    return /(Mac|iPhone|iPod|iPad)/i.test(navigator.platform);
  }, []);

  useEffect(() => {
    console.log('[Header.tsx] translatorStandalone', translatorStandalone);
  }, [translatorStandalone]);
  // Function to toggle the drawer
  const toggleDrawer = useCallback(() => {
    dispatch(GuiStateActions.sideMenuOpen(!sideBarOpenState));
  }, [dispatch, sideBarOpenState]);

  const getUserInitials = useCallback((name: string) => {
    return name
      .split(' ')
      .map((part) => part[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  }, []);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [languageSettingsOpen, setLanguageSettingsOpen] = useState(false);
  const [profileSettingsOpen, setProfileSettingsOpen] = useState(false);
  const [vadSettingsOpen, setVadSettingsOpen] = useState(false);

  // State for Legal Center
  type LegalTab = 'privacy' | 'dpa' | 'terms';
  const [legalDialogState, setLegalDialogState] = useState<{ open: boolean; initialTab: LegalTab | null }>({
    open: false,
    initialTab: null
  });

  const handleClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const isUserMenuOpen = Boolean(anchorEl);
  const commandPalettePlaceholder = translateCommon(nameof.full<I18nCommonNs>((n) => n.commandPalette.placeholder));
  const commandPalettePlaceholderMobile = translateCommon(
    nameof.full<I18nCommonNs>((n) => n.commandPalette.placeholderMobile)
  );
  const macShortcutHint = translateCommon(nameof.full<I18nCommonNs>((n) => n.commandPalette.shortcutHint.mac));
  const windowsShortcutHint = translateCommon(nameof.full<I18nCommonNs>((n) => n.commandPalette.shortcutHint.windows));
  const keyboardShortcut = isMacLike ? macShortcutHint : windowsShortcutHint;

  const openCommandPalette = useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }

    window.dispatchEvent(new CustomEvent('open-command-palette'));
  }, []);

  const handleLegalDialogOpen = (initialTab: LegalTab) => {
    setLegalDialogState({ open: true, initialTab });
    handleClose(); // Close main user menu
  };

  const handleLegalDialogClose = () => {
    setLegalDialogState({ open: false, initialTab: null });
  };

  useEffect(() => {
    const asyncFunc = async () => {
      const photo = await getLoggedInUserPhoto(instance, accounts[0]);
      setUserPhoto(photo);
    };

    if (isAuthenticated && !userPhoto && accounts.length) {
      asyncFunc();
    }
  }, [accounts, instance, isAuthenticated, userPhoto]);

  return (
    <>
      <AppBar elevation={0} sx={{ height: theme.mixins.denseToolbar.minHeight }}>
        <Toolbar
          variant="dense"
          sx={{
            px: 2,
            gap: 2,
            height: '100%'
          }}
        >
          {displaySidebarMenu && !translatorStandalone && (
            <IconButton
              edge="start"
              color="inherit"
              onClick={toggleDrawer}
              aria-label={translateCommon(nameof.full<I18nCommonNs>((l) => l.navigation.open))}
              className="hamburger"
            >
              <MenuIcon />
            </IconButton>
          )}

          <Box sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {isAuthenticated && (
              <>
                <SearchTrigger
                  placeholder={commandPalettePlaceholder}
                  placeholderMobile={commandPalettePlaceholderMobile}
                  ariaLabel={commandPalettePlaceholder}
                  shortcut={keyboardShortcut}
                  onClick={openCommandPalette}
                />
                <ButtonBase
                  onClick={handleClick}
                  sx={{
                    height: 36,
                    width: 36,
                    borderRadius: '50%',
                    overflow: 'hidden'
                  }}
                >
                  <Avatar
                    src={userPhoto}
                    sx={{
                      height: '100%',
                      width: '100%',
                      bgcolor: 'primary.main',
                      fontSize: '0.875rem'
                    }}
                  >
                    {!userPhoto && currentUser?.name ? getUserInitials(currentUser.name) : <Person fontSize="small" />}
                  </Avatar>
                </ButtonBase>
              </>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      <Menu
        open={isUserMenuOpen}
        anchorEl={anchorEl}
        onClose={handleClose} // Keep onClick={handleClose} if individual items shouldn't close the main menu directly
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          sx: {
            width: 280,
            mt: 1,
            '& .MuiMenuItem-root': {
              py: 1,
              px: 2
            }
          }
        }}
      >
        <Box onClick={(e) => e.stopPropagation()}>
          {' '}
          {/* Prevents closing main menu when clicking disabled items */}
          <HeaderMenuItem
            disabled
            icon={
              <Avatar
                src={userPhoto}
                sx={{
                  height: 32,
                  width: 32,
                  bgcolor: 'primary.main',
                  fontSize: '0.75rem',
                  marginLeft: '-4px'
                }}
              >
                {!userPhoto && currentUser?.name ? getUserInitials(currentUser.name) : <Person fontSize="small" />}
              </Avatar>
            }
            primaryText={currentUser?.name ?? user?.name ?? ''}
          />
          <HeaderMenuItem
            disabled
            icon={<Domain sx={{ color: 'text.secondary' }} />}
            primaryText={currentUser?.companyName ?? ''}
            secondaryText={currentUser?.departmentName}
          />
        </Box>

        <Divider sx={{ my: 0.5 }} />

        <HeaderMenuItem
          icon={<Person sx={{ color: 'action.active' }} />}
          primaryText={translateCommon(nameof.full<I18nCommonNs>((n) => n.editProfile))}
          onClick={() => {
            setProfileSettingsOpen(true);
            handleClose();
          }}
        />

        <HeaderMenuItem
          icon={<VolumeUp sx={{ color: 'action.active' }} />}
          primaryText={translateCommon(nameof.full<I18nCommonNs>((n) => n.audioSettings.audioSettings))}
          onClick={() => {
            setVadSettingsOpen(true);
            handleClose();
          }}
        />

        <HeaderMenuItem
          icon={<Language sx={{ color: 'action.active' }} />}
          primaryText={translateCommon(nameof.full<I18nCommonNs>((n) => n.language))}
          onClick={() => {
            setLanguageSettingsOpen(true);
            handleClose();
          }}
        />

        {/* Legal Center Menu Item */}
        <HeaderMenuItem
          icon={<PolicyOutlinedIcon sx={{ color: 'action.active' }} />}
          primaryText={translateCommon(nameof.full<I18nCommonNs>((n) => n.legalCenter.title))}
          onClick={() => handleLegalDialogOpen('privacy')}
        />

        <Divider sx={{ my: 0.5 }} />

        <HeaderMenuItem
          icon={<ExitToApp sx={{ color: 'action.active' }} />}
          primaryText={translateCommon(nameof.full<I18nCommonNs>((n) => n.signOut))}
          onClick={logout} // logout already handles closing if necessary
        />
      </Menu>

      <LanguageSettingsDialog open={languageSettingsOpen} onClose={() => setLanguageSettingsOpen(false)} />
      <EditProfileDialog open={profileSettingsOpen} onClose={() => setProfileSettingsOpen(false)} />
      {vadSettingsOpen && (
        <React.Suspense fallback={null}>
          <VadSettingsModal open={vadSettingsOpen} onClose={() => setVadSettingsOpen(false)} />
        </React.Suspense>
      )}

      {/* Legal Text Dialog */}
      {legalDialogState.open && (
        <LegalTextDialog
          open={legalDialogState.open}
          onClose={handleLegalDialogClose}
          initialTab={legalDialogState.initialTab}
        />
      )}
    </>
  );
};

export default Header;
